import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"

// Model Objects

export class CommunicationSubjectReadOnly extends ModelObject<CommunicationSubjectReadOnly> {

    /**
    * Sequence for ordering communication subjects.
    */
    public sequence: number = 0;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.sequence = jsonSerializedObject.sequence;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.sequence = this.sequence;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "sequence", rule: (v: any) => v == null || v >= -2147483648 || "Sequence is not greater than minimum allowed value: -2147483648"},
            {field: "sequence", rule: (v: any) => v == null || v <= 2147483647 || "Sequence is above maximum allowed value: 2147483647"},
    ];
}


export class CommunicationSubject extends ModelObjectWithDirtyCheck<CommunicationSubject> {

    public version: number = 0;
    public id: Guid = Guid.createEmpty();
    /**
    * Communication Subject Name
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    */
    public name: string = "";
    public isHidden: boolean = false;
    public readOnly: CommunicationSubjectReadOnly = new CommunicationSubjectReadOnly();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.name = jsonSerializedObject.name;
          this.isHidden = jsonSerializedObject.isHidden;
          this.readOnly = new CommunicationSubjectReadOnly(jsonSerializedObject.readOnly);
        }

        this.setDirtyCheckpoint();
    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.id = this.id.toString();
        toRet.name = this.name;
        toRet.isHidden = this.isHidden;
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "CommunicationSubject.Id cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length !== 0 || "Name cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length <= 1000 || "Name has too many characters (1000)"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
    ];
}


export class CommunicationSubjectService extends ServiceBase {

  public async getAllCommunicationSubjects(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(CommunicationSubject)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => response.communicationSubjects.map((v: any) => new CommunicationSubject(v));;
    return this.makeJsonRequest("communicationSubject/v1/getAllCommunicationSubjects", req, false, extractor, opts);
  };

  public async getCommunicationSubject(communicationSubjectId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<CommunicationSubject> {
    const req = {
    tenantId:api.tenantId.toString(),
    communicationSubjectId:communicationSubjectId.toString(),
    };
    const extractor = (response:any) => new CommunicationSubject(response.communicationSubject);;
    return this.makeJsonRequest("communicationSubject/v1/getCommunicationSubject", req, false, extractor, opts);
  };

  public async getCommunicationSubjects(communicationSubjectIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(CommunicationSubject)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    communicationSubjectIds:communicationSubjectIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.communicationSubjects.map((v: any) => new CommunicationSubject(v));;
    return this.makeJsonRequest("communicationSubject/v1/getCommunicationSubjects", req, false, extractor, opts);
  };

  public async saveCommunicationSubject(communicationSubject: CommunicationSubject, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    communicationSubject:communicationSubject.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("communicationSubject/v1/saveCommunicationSubject", req, true, extractor, opts);
  };

  public async setCommunicationSubjectIsHidden(communicationSubjectId: Guid, hide: boolean, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    communicationSubjectId:communicationSubjectId.toString(),
    hide:hide,
    };
    const extractor = null;
    return this.makeJsonRequest("communicationSubject/v1/setCommunicationSubjectIsHidden", req, true, extractor, opts);
  };

  public async updateCommunicationSubjectsSequence(communicationSubjectIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    communicationSubjectIds:communicationSubjectIds.map(v => v.toString()),
    };
    const extractor = null;
    return this.makeJsonRequest("communicationSubject/v1/updateCommunicationSubjectsSequence", req, true, extractor, opts);
  };
}

export const communicationSubjectService = new CommunicationSubjectService();